/**
 * Single source of truth for HTTP 429 (rate-limit / throttle) handling at the
 * raw-`fetch` boundary — TA-10842.
 *
 * The backend throttles repeated attempts (e.g. AllowAuthAttemptsService on
 * login, and the same throttle family on signup). A throttled response is
 * frequently *bodyless*, so calling `response.json()` on it yields `null` or
 * throws — which the old call sites then treated as a silent, false success.
 * Guard the status before treating the body as a normal response, and surface
 * the shared RATE_LIMIT_EXCEEDED copy when the 429 has no message of its own.
 *
 * Keeping this in one place stops the three independent spellings the review
 * flagged (`log-in-stack`, `EmailLogin.web`, `requestPasswordReset`) from
 * drifting apart.
 */

/** i18n key for the user-facing "you're doing that too often" copy (TA-10142). */
export const RATE_LIMIT_ERROR_KEY = 'shared:errors.RATE_LIMIT_EXCEEDED';

/** True when a fetch Response is the backend's rate-limit throttle. */
export const isRateLimited = (response: { status: number }): boolean =>
  response.status === 429;

/** The `error` string a 429 body carries, or null when it is absent/unparseable. */
const readRateLimitMessage = async (
  response: Response
): Promise<string | null> => {
  try {
    const body = await response.json();
    const message = body?.error;
    return typeof message === 'string' && message.trim() ? message : null;
  } catch {
    return null;
  }
};

/**
 * Parse a raw auth/signup `fetch` Response, turning a 429 into a handled
 * `{ error }` shape (matching what the login endpoints return for a business
 * error).
 *
 * A 429 that carries its own `error` keeps it — the LOGIN lockout from
 * AllowAuthAttemptsService says the account is locked for the day, which the
 * generic "wait a moment" copy misstates (TA-10963). Only a bodyless or
 * unparseable 429 falls back to RATE_LIMIT_ERROR_KEY.
 *
 * `t` is injected so this stays a pure, unit-testable function with no direct
 * i18n import.
 */
export const parseAuthResponse = async (
  response: Response,
  t: (key: string) => string
): Promise<{ error: string } | unknown> => {
  if (isRateLimited(response)) {
    return {
      error: (await readRateLimitMessage(response)) ?? t(RATE_LIMIT_ERROR_KEY),
    };
  }
  return response.json();
};

/**
 * Like parseAuthResponse, but for the raw-fetch signup handlers
 * (SignUpWithEmail.web, RegisterWithPhoneNumberForm) whose downstream code
 * reads `response.errors` (e.g. `Object.keys(response.errors)`). The 429 shape
 * carries an empty `errors` object so that access is safe.
 */
export const parseSignupResponse = async (
  response: Response,
  t: (key: string) => string
): Promise<{ error: string; errors: Record<string, unknown> } | unknown> => {
  if (isRateLimited(response)) {
    return { error: t(RATE_LIMIT_ERROR_KEY), errors: {} };
  }
  return response.json();
};
